import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import authService from '../../services/authService';

// Define interfaces for stat data
interface ApiUsage {
  date: string;
  count: number;
  endpoint: string;
  status_code: number;
}

interface DailyUsage {
  date: string;
  total: number;
  successful: number;
  failed: number;
}

interface EndpointUsage {
  endpoint: string;
  count: number;
  percentage: number;
}

interface StatusCodeUsage {
  status_code: number;
  count: number;
  percentage: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#FF6B6B'];

const UsageStatistics: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<number>(30);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage[]>([]);
  const [endpointUsage, setEndpointUsage] = useState<EndpointUsage[]>([]);
  const [statusCodeUsage, setStatusCodeUsage] = useState<StatusCodeUsage[]>([]);
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [successRate, setSuccessRate] = useState<number>(0);

  useEffect(() => {
    fetchUsageStats();
  }, [timeRange]);

  const fetchUsageStats = async () => {
    setLoading(true);
    try {
      if (process.env.REACT_APP_BUILD_MODE === 'development') {
        // Mock data for development
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
        generateMockData();
      } else {
        // Check if authenticated
        if (!authService.isAuthenticated()) {
          setError('Not authenticated. Please log in again.');
          setLoading(false);
          return;
        }

        // Fetch real data in production with auth headers
        const response = await axios.get(`/api/v1/lis-portal/usage-stats?days=${timeRange}`, {
          headers: authService.getAuthHeaders()
        });
        processApiData(response.data);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching usage statistics:', err);
      setError('Failed to fetch usage statistics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = () => {
    // Generate mock daily usage data
    const mockDailyUsage: DailyUsage[] = [];
    const mockRawData: ApiUsage[] = [];
    const today = new Date();
    let totalSuccess = 0;
    let totalFailed = 0;

    // Generate last N days of data
    for (let i = timeRange - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Random counts between 5-100
      const successful = Math.floor(Math.random() * 95) + 5;
      const failed = Math.floor(Math.random() * 20);
      const total = successful + failed;
      
      totalSuccess += successful;
      totalFailed += failed;
      
      mockDailyUsage.push({
        date: dateStr,
        total,
        successful,
        failed
      });
      
      // Generate raw data for this day
      const endpoints = [
        '/api/v1/lis-webhook/standardized/test-results',
        '/api/v1/lis-webhook/standardized/test-results/batch',
        '/api/v1/reports',
        '/api/v1/status'
      ];
      
      const statusCodes = [200, 201, 400, 401, 500];
      
      // Distribute the successful requests across endpoints
      for (let j = 0; j < successful; j++) {
        const endpoint = endpoints[Math.floor(Math.random() * endpoints.length)];
        // Successful status codes: 200, 201
        const statusCode = Math.random() > 0.5 ? 200 : 201;
        
        mockRawData.push({
          date: dateStr,
          endpoint,
          status_code: statusCode,
          count: 1
        });
      }
      
      // Distribute the failed requests across endpoints
      for (let j = 0; j < failed; j++) {
        const endpoint = endpoints[Math.floor(Math.random() * endpoints.length)];
        // Error status codes: 400, 401, 500
        const statusCode = statusCodes[Math.floor(Math.random() * 3) + 2];
        
        mockRawData.push({
          date: dateStr,
          endpoint,
          status_code: statusCode,
          count: 1
        });
      }
    }
    
    // Calculate total requests and success rate
    const total = totalSuccess + totalFailed;
    setTotalRequests(total);
    setSuccessRate(total > 0 ? Math.round((totalSuccess / total) * 100) : 0);
    
    // Process endpoint usage
    const endpointCounts: Record<string, number> = {};
    mockRawData.forEach(item => {
      endpointCounts[item.endpoint] = (endpointCounts[item.endpoint] || 0) + 1;
    });
    
    const processedEndpointUsage: EndpointUsage[] = Object.entries(endpointCounts)
      .map(([endpoint, count]) => ({
        endpoint,
        count,
        percentage: Math.round((count / total) * 100)
      }))
      .sort((a, b) => b.count - a.count);
    
    // Process status code usage
    const statusCodeCounts: Record<number, number> = {};
    mockRawData.forEach(item => {
      statusCodeCounts[item.status_code] = (statusCodeCounts[item.status_code] || 0) + 1;
    });
    
    const processedStatusCodeUsage: StatusCodeUsage[] = Object.entries(statusCodeCounts)
      .map(([code, count]) => ({
        status_code: parseInt(code),
        count,
        percentage: Math.round((count / total) * 100)
      }))
      .sort((a, b) => b.count - a.count);
    
    setDailyUsage(mockDailyUsage);
    setEndpointUsage(processedEndpointUsage);
    setStatusCodeUsage(processedStatusCodeUsage);
  };

  const processApiData = (data: any) => {
    // Process real API data
    setDailyUsage(data.daily_usage || []);
    setEndpointUsage(data.endpoint_usage || []);
    setStatusCodeUsage(data.status_code_usage || []);
    setTotalRequests(data.total_requests || 0);
    setSuccessRate(data.success_rate || 0);
  };

  const formatEndpointName = (endpoint: string) => {
    // Shorten the endpoint name for display
    const parts = endpoint.split('/');
    return parts.slice(parts.length - 2).join('/');
  };

  const getStatusCodeColor = (statusCode: number) => {
    if (statusCode < 300) return '#4caf50'; // Success
    if (statusCode < 400) return '#ff9800'; // Redirect
    if (statusCode < 500) return '#f44336'; // Client Error
    return '#9c27b0'; // Server Error
  };

  const getStatusCodeDescription = (statusCode: number) => {
    switch (statusCode) {
      case 200: return 'OK';
      case 201: return 'Created';
      case 400: return 'Bad Request';
      case 401: return 'Unauthorized';
      case 403: return 'Forbidden';
      case 404: return 'Not Found';
      case 500: return 'Server Error';
      default: return 'Unknown';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">API Usage Statistics</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControl variant="outlined" size="small" sx={{ width: 120, mr: 2 }}>
            <InputLabel id="time-range-label">Time Range</InputLabel>
            <Select
              labelId="time-range-label"
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
              label="Time Range"
            >
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={90}>90 days</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsageStats}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Total Requests
                  </Typography>
                  <Typography variant="h3" component="div">
                    {totalRequests.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Past {timeRange} days
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Success Rate
                  </Typography>
                  <Typography variant="h3" component="div">
                    {successRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Requests with 2xx status
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Daily Average
                  </Typography>
                  <Typography variant="h3" component="div">
                    {totalRequests > 0 ? Math.round(totalRequests / timeRange).toLocaleString() : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Requests per day
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Daily Usage Chart */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily API Requests
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dailyUsage}
                    margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      angle={-45} 
                      textAnchor="end"
                      height={70}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar name="Successful" dataKey="successful" stackId="a" fill="#4caf50" />
                    <Bar name="Failed" dataKey="failed" stackId="a" fill="#f44336" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </CardContent>
          </Card>

          {/* Endpoint and Status Code Distribution */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Endpoint Distribution
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                    <Box sx={{ width: '100%', height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={endpointUsage}
                            dataKey="count"
                            nameKey="endpoint"
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={2}
                            label={({ name, percent }) => 
                              `${formatEndpointName(name as string)}: ${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {endpointUsage.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value, name) => [`${value} requests`, name]} 
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Endpoint</TableCell>
                          <TableCell align="right">Requests</TableCell>
                          <TableCell align="right">Percentage</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {endpointUsage.map((row) => (
                          <TableRow key={row.endpoint}>
                            <TableCell component="th" scope="row" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.endpoint}
                            </TableCell>
                            <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                            <TableCell align="right">{row.percentage}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Status Code Distribution
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                    <Box sx={{ width: '100%', height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusCodeUsage}
                            dataKey="count"
                            nameKey="status_code"
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={2}
                            label={({ name, percent }) => 
                              `${name}: ${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {statusCodeUsage.map((entry) => (
                              <Cell 
                                key={`cell-${entry.status_code}`} 
                                fill={getStatusCodeColor(entry.status_code)} 
                              />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value, name, props) => {
                              const statusCode = Number(name);
                              return [`${value} requests`, `${statusCode} - ${getStatusCodeDescription(statusCode)}`];
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Status Code</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Requests</TableCell>
                          <TableCell align="right">Percentage</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {statusCodeUsage.map((row) => (
                          <TableRow key={row.status_code}>
                            <TableCell component="th" scope="row">
                              {row.status_code}
                            </TableCell>
                            <TableCell>{getStatusCodeDescription(row.status_code)}</TableCell>
                            <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                            <TableCell align="right">{row.percentage}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default UsageStatistics; 